import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, type Ref, ref } from 'lit/directives/ref.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-tab-item')
export class MdcTabItem extends LitElement {
  @property({ type: String, reflect: true }) label = ''
  @property({ type: Boolean, reflect: true, attribute: 'is-active' }) isActive = false

  static styles = css`
		:host {
			display: none;
		}

		:host([isactive]) {
			display: block;
		}
	`

  render() {
    return html`<slot></slot>`
  }
}

@safeCustomElement('mdc-tabs')
export class MdcTabs extends LitElement {
  @property({ type: Number, reflect: true, attribute: 'active-index' }) activeIndex = 0

  @state() private _items: MdcTabItem[] = []

  private _slotRef: Ref<HTMLSlotElement> = createRef()

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-tabs__buttons {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-125, 1.25rem);
			list-style: none;
			padding: 0;
			margin: 0;
		}

		.mdc-tabs__buttons li {
			display: inline;
			margin: 0;
			padding: 0;
		}

		.mdc-tab-item {
			--_mdc-tab-item-underline-gap: 0.625rem;
			--_mdc-tab-item-underline-height: 2px;

			display: inline-flex;
			flex-direction: column;
			align-items: center;
			flex-shrink: 0;
			font-size: var(--text-087-and-half, 0.875rem);
			color: var(--color-functional-gray-200);
			font-weight: 600;
			line-height: 1;
			border-radius: var(--radius-md, 0.5rem);
			padding: var(--spacing-050, 0.5rem) 0;
			gap: var(--_mdc-tab-item-underline-gap);
			border: none;
			background: none;
			cursor: pointer;
			margin: 0;
		}

		.mdc-tab-item:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-tab-item:hover,
		.mdc-tab-item:focus-visible,
		.mdc-tab-item--active {
			color: var(--color-functional-gray-black);
		}

		.mdc-tab-item--active {
			margin-top: calc(var(--_mdc-tab-item-underline-height) + var(--_mdc-tab-item-underline-gap));
		}

		.mdc-tab-item--active::after {
			content: '';
			display: block;
			width: 100%;
			height: var(--_mdc-tab-item-underline-height);
			background-color: var(--color-functional-gray-black);
		}

		.mdc-tabs__content {
			margin-top: var(--spacing-150, 1.5rem);
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			.mdc-tabs__buttons {
				gap: var(--spacing-150, 1.5rem);
			}

			.mdc-tab-item {
				font-size: var(--text-100, 1rem);
			}
		}
	`

  firstUpdated() {
    this.updateActiveStates()
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has('activeIndex')) {
      this.updateActiveStates()
    }
  }

  private handleSlotChange() {
    const slot = this._slotRef.value
    if (!slot) return

    const elements = slot.assignedElements()
    this._items = elements.filter(
      (el): el is MdcTabItem => el.tagName.toLowerCase() === 'mdc-tab-item'
    ) as MdcTabItem[]
    this.updateActiveStates()
  }

  private updateActiveStates() {
    this._items.forEach((item, index) => {
      item.isActive = index === this.activeIndex
    })
  }

  private handleTabClick(index: number) {
    this.activeIndex = index

    const item = this._items[index]
    this.dispatchEvent(
      new CustomEvent('tab-change', {
        detail: { index, label: item?.label || '' },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    return html`
			<div>
				<ul class="mdc-tabs__buttons">
					${this._items.map((item, index) => {
            const label = item.label || ''
            return html`
							<li role="presentation">
								<button
									type="button"
									class="mdc-tab-item ${this.activeIndex === index ? 'mdc-tab-item--active' : ''}"
									@click=${() => this.handleTabClick(index)}
								>
									<span>${label}</span>
								</button>
							</li>
						`
          })}
				</ul>
				<div class="mdc-tabs__content">
					<slot ${ref(this._slotRef)} @slotchange=${this.handleSlotChange}></slot>
				</div>
			</div>
		`
  }
}
